import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  FileText, Layout, Code2, FlaskConical, Rocket, Download, Loader2,
  CheckCircle2, Lock, AlertCircle, ExternalLink, ArrowLeft, Sparkles,
  FolderTree, RefreshCw,
} from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import SEO from '../components/SEO';
import CodeBrowser from '../components/CodeBrowser';
import BuyCreditsModal from '../components/BuyCreditsModal';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { aiAPI, prototypeAPI, buildAPI, authAPI, apiErrorMessage } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { computeStages, STATUS_COLORS, STATUS_LABELS } from '../lib/pipeline';
import { toast } from 'sonner';

const STAGE_META = [
  { key: 'proposal', label: '1 · Proposal', icon: FileText },
  { key: 'prototype', label: '2 · Prototype', icon: Layout },
  { key: 'development', label: '3 · Development', icon: Code2 },
  { key: 'testing', label: '4 · Testing & QA', icon: FlaskConical },
  { key: 'release', label: '5 · Release', icon: Rocket },
];

function StatusBadge({ status }) {
  const icon =
    status === 'ready' || status === 'teaser' ? <CheckCircle2 className="w-3.5 h-3.5" /> :
    status === 'generating' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> :
    status === 'failed' ? <AlertCircle className="w-3.5 h-3.5" /> :
    status === 'locked' ? <Lock className="w-3.5 h-3.5" /> : <Sparkles className="w-3.5 h-3.5" />;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide"
      style={{ color: STATUS_COLORS[status], background: `${STATUS_COLORS[status]}1A` }}
      data-testid={`stage-status-${status}`}
    >
      {icon} {STATUS_LABELS[status]}
    </span>
  );
}

function StageSection({ meta, status, children }) {
  const Icon = meta.icon;
  return (
    <section
      className="rounded-xl border p-6"
      style={{ borderColor: 'var(--border)', background: 'var(--card-bg)', opacity: status === 'locked' ? 0.65 : 1 }}
      data-testid={`stage-${meta.key}`}
    >
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'var(--tag-bg)' }}>
            <Icon className="w-4.5 h-4.5" style={{ color: 'var(--primary-accent)' }} />
          </div>
          <h2 className="font-heading font-bold text-lg" style={{ color: 'var(--text-primary)' }}>{meta.label}</h2>
        </div>
        <StatusBadge status={status} />
      </div>
      {children}
    </section>
  );
}

const downloadBlob = (res, filename) => {
  const url = URL.createObjectURL(res.data);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

export default function ProjectDetailPage() {
  const { leadId } = useParams();
  const { refreshUser } = useAuth();
  const [lead, setLead] = useState(null);
  const [prototypes, setPrototypes] = useState([]);
  const [builds, setBuilds] = useState([]);
  const [costs, setCosts] = useState({});
  const [loading, setLoading] = useState(true);
  const [showBuy, setShowBuy] = useState(false);
  const [showCode, setShowCode] = useState(false);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const [leadRes, protoRes, buildRes] = await Promise.all([
        aiAPI.getLead(leadId),
        prototypeAPI.listForLead(leadId),
        buildAPI.listForLead(leadId),
      ]);
      setLead(leadRes.data);
      setPrototypes(protoRes.data || []);
      setBuilds(buildRes.data || []);
    } catch {
      toast.error('Failed to load project');
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    load();
    authAPI.getCreditConfig().then((r) => setCosts(r.data)).catch(() => {});
  }, [load]);

  const stages = computeStages({ lead, prototypes, builds });
  const build = stages.build;
  const anyGenerating =
    stages.prototype === 'generating' || stages.development === 'generating' || stages.testing === 'generating';

  useEffect(() => {
    if (!anyGenerating) return undefined;
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [anyGenerating, load]);

  const run = async (key, fn, successMsg) => {
    setBusy(key);
    try {
      await fn();
      if (successMsg) toast.success(successMsg);
      await load();
      await refreshUser();
    } catch (e) {
      const detail = apiErrorMessage(e, 'Something went wrong');
      toast.error(detail);
      if (e.response?.status === 402) setShowBuy(true);
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--background)' }}>
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--primary-accent)' }} />
      </div>
    );
  }
  if (!lead) {
    return (
      <div className="min-h-screen" style={{ background: 'var(--background)', color: 'var(--text-primary)' }}>
        <Navbar />
        <div className="max-w-3xl mx-auto px-6 py-24 text-center">
          <h1 className="font-heading text-2xl font-bold mb-3">Project not found</h1>
          <Link to="/workspace" className="hover:underline" style={{ color: 'var(--primary-accent)' }}>Back to workspace</Link>
        </div>
      </div>
    );
  }

  const title = build?.project_name && build.status === 'ready' ? build.project_name : (lead.project_type || 'Project');

  return (
    <div className="min-h-screen" style={{ background: 'var(--background)', color: 'var(--text-primary)' }}>
      <SEO title={`${title} | Workspace | XceedAgents.ai`} description="Project lifecycle pipeline" />
      <Navbar />

      <main className="max-w-5xl mx-auto px-6 py-10" data-testid="project-detail-page">
        <Link to="/workspace" className="inline-flex items-center gap-1.5 text-sm mb-6 hover:underline" style={{ color: 'var(--text-muted)' }} data-testid="back-to-workspace">
          <ArrowLeft className="w-4 h-4" /> All projects
        </Link>

        <div className="mb-10">
          <h1 className="font-heading text-3xl sm:text-4xl font-bold capitalize">{title}</h1>
          <p className="mt-2 text-sm max-w-3xl" style={{ color: 'var(--text-muted)' }}>{lead.description}</p>
        </div>

        <div className="space-y-5">
          {/* ---------- 1. Proposal ---------- */}
          <StageSection meta={STAGE_META[0]} status={stages.proposal}>
            <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
              AI-generated scope, timeline and commercial proposal for your project.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to={`/proposal/${lead.id}`}>
                <Button variant="outline" className="gap-2" data-testid="view-proposal-btn">
                  <ExternalLink className="w-4 h-4" /> View Proposal
                </Button>
              </Link>
              {stages.proposal === 'ready' && (
                <Button
                  variant="outline"
                  className="gap-2"
                  disabled={busy === 'pdf'}
                  onClick={() => run('pdf', async () => {
                    const res = await aiAPI.downloadProposalPDF(lead.id);
                    downloadBlob(res, `xceedagents-proposal-${lead.id}.pdf`);
                  })}
                  data-testid="download-proposal-pdf-btn"
                >
                  {busy === 'pdf' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Download PDF
                </Button>
              )}
            </div>
          </StageSection>

          {/* ---------- 2. Prototype ---------- */}
          <StageSection meta={STAGE_META[1]} status={stages.prototype}>
            <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
              Clickable multi-page prototype of your product. Teaser landing page is free —
              the full interactive prototype costs {costs.prototype_cost_credits ?? 150} credits.
            </p>
            <div className="flex flex-wrap gap-3">
              {stages.teaser?.status === 'ready' ? (
                <Link to={`/prototype-preview/${stages.teaser.id}`}>
                  <Button variant="outline" className="gap-2" data-testid="view-teaser-btn">
                    <ExternalLink className="w-4 h-4" /> View Free Teaser
                  </Button>
                </Link>
              ) : (
                <Button
                  variant="outline"
                  className="gap-2"
                  disabled={busy === 'teaser' || stages.teaser?.status === 'generating'}
                  onClick={() => run('teaser', () => prototypeAPI.generateTeaser(lead.id), 'Teaser generation started')}
                  data-testid="generate-teaser-btn"
                >
                  {stages.teaser?.status === 'generating' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Layout className="w-4 h-4" />}
                  {stages.teaser?.status === 'generating' ? 'Generating teaser…' : 'Generate Free Teaser'}
                </Button>
              )}
              {stages.fullProto?.status === 'ready' ? (
                <Link to={`/prototype-preview/${stages.fullProto.id}`}>
                  <Button className="gap-2 font-semibold text-white" style={{ background: 'var(--primary-accent)' }} data-testid="view-full-prototype-btn">
                    <ExternalLink className="w-4 h-4" /> Open Full Prototype
                  </Button>
                </Link>
              ) : (
                <Button
                  className="gap-2 font-semibold text-white"
                  style={{ background: 'var(--primary-accent)' }}
                  disabled={busy === 'full' || stages.fullProto?.status === 'generating'}
                  onClick={() => run('full', () => prototypeAPI.generateFull(lead.id), 'Full prototype generation started')}
                  data-testid="generate-full-prototype-btn"
                >
                  {stages.fullProto?.status === 'generating' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  {stages.fullProto?.status === 'generating'
                    ? 'Building full prototype…'
                    : `Full Prototype (${costs.prototype_cost_credits ?? 150} cr)`}
                </Button>
              )}
            </div>
          </StageSection>

          {/* ---------- 3. Development ---------- */}
          <StageSection meta={STAGE_META[2]} status={stages.development}>
            <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
              Our AI engineering agents write your actual production codebase — React frontend,
              Python FastAPI backend and MongoDB data layer — reviewed file by file, downloadable as source.
            </p>

            {!build && (
              <Button
                className="gap-2 font-semibold text-white"
                style={{ background: 'var(--primary-accent)' }}
                disabled={busy === 'build'}
                onClick={() => run('build', () => buildAPI.generate(lead.id), 'Development build started — this takes 5-10 minutes')}
                data-testid="generate-build-btn"
              >
                {busy === 'build' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Code2 className="w-4 h-4" />}
                Generate Full-Stack Build ({costs.dev_build_cost_credits ?? 500} cr)
              </Button>
            )}

            {build?.status === 'generating' && (
              <div className="space-y-2" data-testid="build-progress">
                <div className="flex items-center justify-between text-sm">
                  <span className="flex items-center gap-2" style={{ color: 'var(--text-secondary)' }}>
                    <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--primary-accent)' }} />
                    {build.current_step || 'Working…'}
                  </span>
                  <span className="font-bold" style={{ color: 'var(--primary-accent)' }}>{build.progress || 0}%</span>
                </div>
                <Progress value={build.progress || 0} className="h-2" />
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  AI agents are writing your code in phases (architecture → backend → frontend → docs). Safe to leave this page.
                </p>
              </div>
            )}

            {build?.status === 'failed' && (
              <div className="space-y-3">
                <p className="text-sm" style={{ color: 'var(--error)' }}>Build failed: {build.error}</p>
                <Button
                  variant="outline"
                  className="gap-2"
                  disabled={busy === 'build'}
                  onClick={() => run('build', () => buildAPI.generate(lead.id), 'Retrying development build')}
                  data-testid="retry-build-btn"
                >
                  <RefreshCw className="w-4 h-4" /> Retry Build
                </Button>
              </div>
            )}

            {build?.status === 'ready' && (
              <div className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  {Object.values(build.stack || {}).map((s) => (
                    <span key={s} className="px-2.5 py-1 rounded-full text-xs font-semibold" style={{ background: 'var(--tag-bg)', color: 'var(--primary-accent)' }}>
                      {s}
                    </span>
                  ))}
                  <span className="px-2.5 py-1 rounded-full text-xs font-semibold" style={{ background: 'var(--surface-elevated)', color: 'var(--text-secondary)' }}>
                    {(build.files_created || []).length} files
                  </span>
                </div>
                {build.summary && <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{build.summary}</p>}
                <div className="flex flex-wrap gap-3">
                  <Button variant="outline" className="gap-2" onClick={() => setShowCode(!showCode)} data-testid="browse-code-btn">
                    <FolderTree className="w-4 h-4" /> {showCode ? 'Hide Code' : 'Browse Code'}
                  </Button>
                  <Button
                    className="gap-2 font-semibold text-white"
                    style={{ background: 'var(--primary-accent)' }}
                    disabled={busy === 'download'}
                    onClick={() => run('download', async () => {
                      const res = await buildAPI.download(build.id);
                      downloadBlob(res, 'source.zip');
                    })}
                    data-testid="download-source-btn"
                  >
                    {busy === 'download' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Download Source ZIP
                  </Button>
                </div>
                {showCode && <CodeBrowser buildId={build.id} files={build.files_created || []} />}
              </div>
            )}
          </StageSection>

          {/* ---------- 4. Testing ---------- */}
          <StageSection meta={STAGE_META[3]} status={stages.testing}>
            <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
              AI QA agents review every file of your build — API endpoints, data validation, UX flows,
              security and code quality — and produce a structured test report.
            </p>

            {stages.testing === 'locked' && (
              <p className="text-sm font-medium" style={{ color: 'var(--text-muted)' }}>
                Complete the Development build to unlock testing.
              </p>
            )}

            {(stages.testing === 'available' || stages.testing === 'failed') && (
              <div className="space-y-3">
                {build?.test_error && <p className="text-sm" style={{ color: 'var(--error)' }}>{build.test_error}</p>}
                <Button
                  className="gap-2 font-semibold text-white"
                  style={{ background: 'var(--primary-accent)' }}
                  disabled={busy === 'test'}
                  onClick={() => run('test', () => buildAPI.runTests(build.id), 'AI QA review started')}
                  data-testid="run-tests-btn"
                >
                  {busy === 'test' ? <Loader2 className="w-4 h-4 animate-spin" /> : <FlaskConical className="w-4 h-4" />}
                  Run AI QA & Test Report ({costs.test_report_cost_credits ?? 200} cr)
                </Button>
              </div>
            )}

            {stages.testing === 'generating' && (
              <p className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--primary-accent)' }} />
                QA agents are reviewing your codebase… (1-2 minutes)
              </p>
            )}

            {stages.testing === 'ready' && build?.test_report && (
              <div className="space-y-5" data-testid="test-report">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { label: 'Quality Score', value: `${build.test_report.quality_score}/100`, color: 'var(--primary-accent)' },
                    { label: 'Passed', value: build.test_report.passed, color: '#10B981' },
                    { label: 'Warnings', value: build.test_report.warnings, color: '#F59E0B' },
                    { label: 'Failed', value: build.test_report.failed, color: '#EF4444' },
                  ].map((s) => (
                    <div key={s.label} className="rounded-lg p-4 text-center" style={{ background: 'var(--surface)' }}>
                      <p className="font-heading text-2xl font-bold" style={{ color: s.color }}>{s.value}</p>
                      <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{s.label}</p>
                    </div>
                  ))}
                </div>
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{build.test_report.summary}</p>
                <div className="space-y-4">
                  {(build.test_report.categories || []).map((cat) => (
                    <div key={cat.name}>
                      <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: 'var(--text-muted)' }}>{cat.name}</p>
                      <div className="space-y-1.5">
                        {(cat.checks || []).map((check, i) => (
                          <div key={i} className="flex items-start gap-2 text-sm">
                            {check.status === 'pass' ? <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-emerald-500" /> :
                             check.status === 'warn' ? <AlertCircle className="w-4 h-4 mt-0.5 shrink-0 text-amber-500" /> :
                             <AlertCircle className="w-4 h-4 mt-0.5 shrink-0 text-red-500" />}
                            <span style={{ color: 'var(--text-secondary)' }}>
                              <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{check.name}</span>
                              {check.note ? ` — ${check.note}` : ''}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                {(build.test_report.recommendations || []).length > 0 && (
                  <div className="rounded-lg p-4" style={{ background: 'var(--tag-bg)' }}>
                    <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: 'var(--primary-accent)' }}>Recommendations</p>
                    <ul className="list-disc list-inside space-y-1 text-sm" style={{ color: 'var(--text-secondary)' }}>
                      {build.test_report.recommendations.map((r, i) => <li key={i}>{r}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </StageSection>

          {/* ---------- 5. Release ---------- */}
          <StageSection meta={STAGE_META[4]} status={stages.release}>
            <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>
              Final release package for hosting: complete source, deployment guide, QA report and
              go-live checklist — everything you need to put your product in production.
            </p>

            {stages.release === 'locked' && (
              <p className="text-sm font-medium" style={{ color: 'var(--text-muted)' }}>
                Complete the Development build to unlock the release package.
              </p>
            )}

            {stages.release === 'available' && (
              <Button
                className="gap-2 font-semibold text-white"
                style={{ background: 'var(--primary-accent)' }}
                disabled={busy === 'release'}
                onClick={() => run('release', () => buildAPI.packageRelease(build.id), 'Release package ready!')}
                data-testid="package-release-btn"
              >
                {busy === 'release' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
                Package Release ({costs.release_cost_credits ?? 100} cr)
              </Button>
            )}

            {stages.release === 'ready' && (
              <div className="flex flex-wrap gap-3 items-center">
                <Button
                  className="gap-2 font-semibold text-white"
                  style={{ background: '#10B981' }}
                  disabled={busy === 'release-dl'}
                  onClick={() => run('release-dl', async () => {
                    const res = await buildAPI.downloadRelease(build.id);
                    downloadBlob(res, 'release.zip');
                  })}
                  data-testid="download-release-btn"
                >
                  {busy === 'release-dl' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  Download Release Package
                </Button>
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                  Includes source, RELEASE_NOTES.md, DEPLOYMENT.md and QA report.
                </p>
              </div>
            )}
          </StageSection>
        </div>
      </main>

      <BuyCreditsModal open={showBuy} onOpenChange={setShowBuy} />
    </div>
  );
}
